import { get, toString, toInteger, omit, trim } from 'lodash';
import { HttpService } from 'app/services';
import * as AppActions from 'app/store/actions';
import i18n from 'i18n';
import { parseError } from 'helpers/Error';
import { DISPENSING_SYSTEM } from 'app/main/pharmacy/constants';
import { arrayToObject } from 'app/utils/Utils';
import { startDispenseSyncJobPolling, stopDispenseSyncJobPolling } from '../../utils/dispenseSyncJobPolling';

const DISPENSING_SYSTEM_VALUE = arrayToObject(DISPENSING_SYSTEM, 'value');

export const GET_PATIENT_GROUP = '@PATIENT_GROUP/GET_PATIENT_GROUP';
export const GET_PATIENT_GROUP_SUCCESS = '@PATIENT_GROUP/GET_PATIENT_GROUP_SUCCESS';
export const GET_PATIENT_GROUP_FAIL = '@PATIENT_GROUP/GET_PATIENT_GROUP_FAIL';
// packOptions
export const GET_PACK_OPTION = '@PATIENT_GROUP/GET_PACK_OPTION';
export const GET_PACK_OPTION_SUCCESS = '@PATIENT_GROUP/GET_PACK_OPTION_SUCCESS';
export const GET_PACK_OPTION_FAIL = '@PATIENT_GROUP/GET_PACK_OPTION_FAIL';

export const DELETE_PATIENT_GROUP_START = '@PATIENT_GROUP/DELETE_PATIENT_GROUP_START';
export const DELETE_PATIENT_GROUP_DONE = '@PATIENT_GROUP/DELETE_PATIENT_GROUP_DONE';

export const UPDATE_PATIENT_GROUP = '@PATIENT_GROUP/UPDATE_PATIENT_GROUP';
export const UPDATE_PATIENT_GROUP_SUCCESS = '@PATIENT_GROUP/UPDATE_PATIENT_GROUP_SUCCESS';
export const UPDATE_PATIENT_GROUP_FAIL = '@PATIENT_GROUP/UPDATE_PATIENT_GROUP_FAIL';

export const CHANGE_PATIENT_GROUP_UPDATE_FORM = '@PATIENT_GROUP/CHANGE_PATIENT_GROUP_UPDATE_FORM';
// packOptions
export const UPDATE_PACK_OPTION = '@PATIENT_GROUP/UPDATE_PACK_OPTION';
export const UPDATE_PACK_OPTION_SUCCESS = '@PATIENT_GROUP/UPDATE_PACK_OPTION_SUCCESS';
export const UPDATE_PACK_OPTION_FAIL = '@PATIENT_GROUP/UPDATE_PACK_OPTION_FAIL';

export const UPDATE_PATIENT_GROUP_STATUS = '@PATIENT_GROUP/UPDATE_PATIENT_GROUP_STATUS';
export const UPDATE_PATIENT_GROUP_STATUS_SUCCESS = '@PATIENT_GROUP/UPDATE_PATIENT_GROUP_STATUS_SUCCESS';
export const UPDATE_PATIENT_GROUP_STATUS_FAIL = '@PATIENT_GROUP/UPDATE_PATIENT_GROUP_STATUS_FAIL';

export const CLEAR_UPDATE_FORM = '@PATIENT_GROUP/CLEAR_UPDATE_FORM';

// Visual icons (from /api/foils/languages)
export const UPDATE_VISUAL_ICONS = '@PATIENT_GROUP/UPDATE_VISUAL_ICONS';

export function updateVisualIcons(iconIds = []) {
  return (dispatch) => {
    dispatch({ type: UPDATE_VISUAL_ICONS, payload: { iconIds } });
  };
}

export function getPatientGroup(patGroupId) {
  return (dispatch) => {
    dispatch({ type: GET_PATIENT_GROUP });
    HttpService.get(`api/patientGroups/${patGroupId}`)
      .then((resp) => {
        dispatch({ type: GET_PATIENT_GROUP_SUCCESS, payload: { data: parseModelToData(resp.data) } });
      })
      .catch((err) => {
        const { errorFields, errorMessages } = parseError(err);
        dispatch({
          type: GET_PATIENT_GROUP_FAIL,
          payload: { message: errorMessages.join('\n'), error: errorFields },
        });
        dispatch(AppActions.createDialogMessage('error', 'Error', errorMessages));
      });
  };
}

export function confirmUpdatePatientGroup(isTimeSlots = false) {
  return async (dispatch, getState) => {
    const { data, initialDispenseCode, id } = getState().patientGroup.update;
    const authUser = getState().auth.user;
    const newDispenseCode = trim(data?.dispenseCode || '');
    const oldDispenseCode = trim(initialDispenseCode || '');
    const isFDPlus = toString(authUser?.dispensingSystem) === DISPENSING_SYSTEM_VALUE['13'].value;
    const dispenseCodeChanged = newDispenseCode.toLowerCase() !== oldDispenseCode.toLowerCase();

    if (isTimeSlots || !isFDPlus || !dispenseCodeChanged || !newDispenseCode) {
      dispatch(updatePatientGroup(isTimeSlots, { runDispenseSyncImmediately: false }));
      return;
    }

    let isNewDispenseCodeAvailable = false;
    try {
      const availability = await HttpService.get('api/patientGroups/availability/dispense-code', {
        code: newDispenseCode,
        excludePatientGroupId: id,
      });
      isNewDispenseCodeAvailable = Boolean(
        get(availability, 'data.isAvailable', get(availability, 'Data.IsAvailable', get(availability, 'isAvailable'))),
      );
    } catch (e) {
      dispatch(updatePatientGroup(isTimeSlots, { runDispenseSyncImmediately: false }));
      return;
    }

    if (!isNewDispenseCodeAvailable) {
      dispatch(updatePatientGroup(isTimeSlots, { runDispenseSyncImmediately: false }));
      return;
    }

    dispatch(
      AppActions.createDialogMessage(
        'info',
        i18n.t('patientGroup:CREATE_RUN_DISPENSE_SYNC_CONFIRM_TITLE'),
        i18n.t('patientGroup:CREATE_RUN_DISPENSE_SYNC_CONFIRM_MESSAGE'),
        {
          dialogId: 'PATIENT_GROUP_UPDATE_RUN_DISPENSE_SYNC',
          options: {
            buttons: [
              {
                label: i18n.t('NO'),
              },
              {
                label: i18n.t('YES'),
                color: 'primary',
                variant: 'contained',
              },
            ],
          },
          callback: (label) => {
            const runDispenseSyncImmediately = label === i18n.t('YES');
            dispatch(updatePatientGroup(isTimeSlots, { runDispenseSyncImmediately }));
            dispatch(AppActions.closeDialogMessage('PATIENT_GROUP_UPDATE_RUN_DISPENSE_SYNC'));
          },
        },
      ),
    );
  };
}

export function updatePatientGroup(isTimeSlots = false, { runDispenseSyncImmediately = false } = {}) {
  return (dispatch, getState) => {
    const { data, id, initialDispenseCode } = getState().patientGroup.update;
    dispatch({ type: UPDATE_PATIENT_GROUP });
    const postData = parseDataToModel(data);

    delete postData.id;
    if (!isTimeSlots) {
      postData.runDispenseSyncImmediately = Boolean(runDispenseSyncImmediately);
      const newDispenseCode = trim(data?.dispenseCode || '');
      const oldDispenseCode = trim(initialDispenseCode || '');
      if (newDispenseCode.toLowerCase() !== oldDispenseCode.toLowerCase()) {
        stopDispenseSyncJobPolling();
      }
    }
    const url = isTimeSlots ? `api/patientGroups/timeslots/${id}` : `api/patientGroups/${id}`;

    HttpService.put(url, postData)
      .then((resp) => {
        const jobId = resp?.dispenseSyncJobId || resp?.DispenseSyncJobId;
        dispatch(
          AppActions.showMessage({
            message: 'Patient group has been updated successfully',
            error: false,
          }),
        );
        dispatch({ type: UPDATE_PATIENT_GROUP_SUCCESS });

        if (jobId) {
          startDispenseSyncJobPolling({ jobId, intervalMs: 5000 });
        }
      })
      .catch((err) => {
        const { errorMessages } = parseError(err);
        dispatch({
          type: UPDATE_PATIENT_GROUP_FAIL,
          payload: { message: errorMessages.join('\n'), error: true },
        });
        dispatch(AppActions.createDialogMessage('error', 'Warning', errorMessages));
      });
  };
}

export function deletePatientGroup(patGroupId, callBack) {
  return (dispatch) => {
    dispatch({ type: DELETE_PATIENT_GROUP_START });
    HttpService.delete(`api/patientGroups/${patGroupId}`)
      .then(() => {
        dispatch({ type: DELETE_PATIENT_GROUP_DONE });
        dispatch(
          AppActions.showMessage({
            message: 'patientGroup:DELETE_SUCCESS_MESSAGE',
          }),
        );
        // eslint-disable-next-line no-unused-expressions
        callBack && callBack();
      })
      .catch((err) => {
        const { errorMessages } = parseError(err);
        dispatch({ type: DELETE_PATIENT_GROUP_DONE });
        dispatch(AppActions.createDialogMessage('error', 'Warning', errorMessages));
      });
  };
}

export function clearUpdateForm() {
  return (dispatch) => {
    dispatch({ type: CLEAR_UPDATE_FORM });
  };
}

export function changePatientGroupUpdateForm(data) {
  return (dispatch) => {
    dispatch({ type: CHANGE_PATIENT_GROUP_UPDATE_FORM, payload: { data: { ...data, beds: toInteger(data.beds) } } });
  };
}

export function updatePatientGroupStatus(status) {
  return (dispatch, getState) => {
    const { id } = getState().patientGroup.update;
    dispatch({ type: UPDATE_PATIENT_GROUP_STATUS });

    const url = status ? `api/patientgroups/${id}/active` : `api/patientgroups/${id}/inactive`;

    HttpService.put(url)
      .then(() => {
        dispatch(
          AppActions.showMessage({
            message: 'Patient group has been updated successfully',
          }),
        );
        dispatch({ type: UPDATE_PATIENT_GROUP_STATUS_SUCCESS, payload: status });
      })
      .catch((err) => {
        const { errorFields, errorMessages } = parseError(err);
        dispatch({
          type: UPDATE_PATIENT_GROUP_STATUS_FAIL,
          payload: { message: errorMessages.join('\n'), error: errorFields },
        });
        dispatch(AppActions.createDialogMessage('error', 'Error', errorMessages));
      });
  };
}

function parseModelToData(data) {
  return {
    ...data,
    email: get(data.email, [0], ''),
    phone: get(data.phone, [0], ''),
    isListPatientByRoom: toString(data.isListPatientByRoom), // Parse value to match with constants
    isUseGenericAsDrugName: toString(data.isUseGenericAsDrugName), // Parse value to match with constants
    defaultTimeslot: fillTimeslot(data.defaultTimeslot || [], 4),
    sachetTimeslot: fillTimeslot(data.sachetTimeslot || [], 8),
    shortCourseTimeslot: fillTimeslot(data.shortCourseTimeslot || [], 8),
    status: data.status === 0,
    isAutomation: toString(data.isAutomation),
    isVirtualPillBalance:
      typeof data.isVirtualPillBalance === 'undefined' ? 'true' : toString(data.isVirtualPillBalance),
  };
}

export function parseDataToModel(data) {
  return omit(
    {
      ...data,
      beds: data.beds || 0,
      email: [data.email],
      phone: [data.phone],
      isListPatientByRoom: data.isListPatientByRoom === 'true',
      isUseGenericAsDrugName: data.isUseGenericAsDrugName === 'true',
      isVirtualPillBalance: data.isVirtualPillBalance === 'true',
    },
    'status',
  );
}

function fillTimeslot(timeSlots, numOfSlot) {
  return Array(numOfSlot)
    .fill('')
    .reduce((prev, cur, index) => {
      return [...prev, timeSlots[index] || cur];
    }, []);
}

// packOption
export function getPackOption(patGroupId) {
  return (dispatch) => {
    dispatch({ type: GET_PACK_OPTION });
    HttpService.get(`/api/patientGroups/${patGroupId}/print-options`)
      .then((resp) => {
        dispatch({ type: GET_PACK_OPTION_SUCCESS, payload: { data: resp.data } });
      })
      .catch((err) => {
        const { errorFields, errorMessages, errorTitle } = parseError(err);
        dispatch({
          type: GET_PACK_OPTION_FAIL,
          payload: { message: errorMessages.join('\n'), error: errorFields },
        });
        dispatch(AppActions.createDialogMessage('error', errorTitle, errorMessages));
      });
  };
}

export function updatePackOption(id, data) {
  return (dispatch) => {
    dispatch({ type: UPDATE_PACK_OPTION });
    return HttpService.put(`/api/patientGroups/${id}/print-options`, data)
      .then(() => {
        dispatch(
          AppActions.showMessage({
            message: 'patientGroup:UPDATE_PACK_OPTIONS_SUCCESS_MESSAGE',
          }),
        );
        dispatch({ type: UPDATE_PACK_OPTION_SUCCESS, payload: { data } });
        return true;
      })
      .catch((err) => {
        const { errorFields, errorMessages, errorTitle } = parseError(err);
        dispatch({
          type: UPDATE_PACK_OPTION_FAIL,
          payload: { message: errorMessages.join('\n'), error: errorFields },
        });
        dispatch(AppActions.createDialogMessage('error', errorTitle, errorMessages));
      });
  };
}
